import React from 'react';
import { Form } from 'react-final-form';
import memoize from 'lru-memoize';
import { components } from 'react-select';

import { createValidator, required, url, auth } from '../../../../redux/utils/validation';
import { webhooksActions, eventTypes } from '../../../../redux/reducers/webhooks';
import { useAppSelector, useAppDispatch } from '../../../../hooks/useRedux';

import { ModalContainer, Button, Tooltip } from '../../../../elements';
import { FormInput, FormSelect } from '../../../../elements/Form';
import { showError, showSuccess } from '../../../../elements/Toasts/toastActions';
import modal from '../../../../elements/ModalContainer/ModalContainer.module.scss';

import settings from '../../Settings.module.scss';
import strings from '../../../../lang/lang';
import { ReactComponent as InfoIcon } from '../../../../styles/icon/info.svg';

interface IModal {
  closeModal: (v?: object) => any;
}

const IconOption = (props) => (
  <components.Option {...props}>
    <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
      {props.data.isDisabled ? (
        <>
          <p style={{ color: 'grey' }}>{props.data.label}</p>
          <Tooltip
            className={settings.settings__tooltip}
            content={'You already have a webhook with this type'}
          >
            <InfoIcon style={{ marginLeft: 10 }} />
          </Tooltip>
        </>
      ) : (
        <p>{props.data.label}</p>
      )}
    </div>
  </components.Option>
);

function CreateWebhookModal(props: IModal) {
  const { closeModal } = props;
  const dispatch = useAppDispatch();
  const allEventTypes = useAppSelector(eventTypes);

  function onSubmit({ newUrl, eventTypes }) {
    const data = { url: newUrl, eventTypeId: eventTypes.value };
    return new Promise((res, rej) => {
      dispatch(webhooksActions.createWebhook({ data, res, rej }));
    })
      .then(() => {
        showSuccess(strings.appWebhooks.text.created);
      })
      .catch(({ data }) => {
        if (data && data.message) {
          if (data.message === 'Webhook url is taken by another partner') {
            showError(strings.appWebhooks.errors.urlUsedException);
          } else if (data.message === 'The partner is blocked!') {
            showError(strings.appWebhooks.errors.partnerBlockedException);
          } else {
            showError(data.message);
          }
        } else {
          showError(strings.appWebhooks.errors.creatingException);
        }
      })
      .finally(() => {
        closeModal({});
      });
  }

  const validate = memoize(10)(
    createValidator({
      newUrl: [required, url, auth],
      eventTypes: [required],
    })
  );

  const options = allEventTypes.map((c) => ({
    value: c.id,
    label: c.name,
    isDisabled: c.used,
  }));

  return (
    <ModalContainer
      isOpen
      closeModalWindow={() => closeModal({})}
      title={strings.appWebhooks.text.titleCreate}
    >
      <Form
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit, hasValidationErrors }) => {
          return (
            <form onSubmit={handleSubmit}>
              <FormInput
                id="newUrl"
                name="newUrl"
                label={strings.appWebhooks.fields.newUrl}
                autoFocus
              />
              <FormSelect
                options={options}
                name="eventTypes"
                label={strings.appWebhooks.fields.newEventType}
                components={{ Option: IconOption }}
              />
              <div className={modal['react-modal__btn-box']}>
                <Button
                  name={strings.appWebhooks.buttons.createNew}
                  variant="primary"
                  secondaryClassName="popup_primary"
                  type="submit"
                  disabled={hasValidationErrors}
                />
              </div>
              <div className={modal['react-modal__btn-box']}>
                <Button
                  name={strings.appWebhooks.buttons.cancel}
                  variant="secondary"
                  secondaryClassName="popup_secondary"
                  type="button"
                  onClick={() => closeModal({})}
                />
              </div>
            </form>
          );
        }}
      />
    </ModalContainer>
  );
}

export default CreateWebhookModal;
